import React from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import {
  IntlProvider,
  FormattedNumber,
  FormattedNumberParts,
  useIntl,
} from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import RichText from '../../../UI/RichText';
import InventoryLogos from '../InventoryBadges/InventoryBadges';
import getPhoneInventoryVisibility from '../../../../utils/phoneStatus/getPhoneInventoryVisibility';

import { InventoryStatus, NearbyStore, ReserveStatus } from '../../../../types';
import { FeaturedPhoneBackImage, InventoryLabels, Offer } from '../../../../ContentfulTypes';
import {
  CarrierOfferForPhone,
  Legend,
  BonusOffer,
  TelecomProvider,
  Preview,
  PreviewWrapper,
  TelecomLogo,
  Detail,
  PriceLarge,
  Price,
  Disclaimer,
  ButtonGraphicWrapper,
  ButtonGraphic,
  DetailLabel,
  ExpandedTierDetails,
  AccordionWrapper,
  AccordionDualPricing,
  AccordionHeading,
  AccordionButton,
  InventoryWrapper,
} from './styles';
import PcoPointsLabel from './PcoPointsLabel';

interface Props {
  offer: Offer;
  labels: Record<string, string>;
  activeStore?: NearbyStore;
  inventoryLabels?: InventoryLabels;
  apiError: boolean;
  inventoryStatus: InventoryStatus;
  selectedCarrier?: string;
  reserveStatus: ReserveStatus;
}

// Section heading rich text options
const tierBodyOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
  },
};

const CarrierPricingDetails = ({
  offer,
  labels,
  activeStore,
  inventoryLabels,
  apiError,
  inventoryStatus,
  selectedCarrier,
  reserveStatus,
}: Props) => {
  const { locale } = useIntl();
  const renderInventory = getPhoneInventoryVisibility({
    reserveStatus,
  });

  return (
    <CarrierOfferForPhone key={uuidv4()}>
      <Legend>
        {/* LOGO */}
        <TelecomProvider>
          <TelecomLogo image={offer?.phoneCarrier?.logo as FeaturedPhoneBackImage} />
        </TelecomProvider>
        {offer?.pcoPoints > 0 && (
          <BonusOffer>
            {labels['53uqFmBxOLoJEbF08Zlk2V']}
            <span>
              <FormattedNumber value={offer?.pcoPoints} />
            </span>
            {locale === "en" && <PcoPointsLabel points={offer.pcoPoints} labels={labels} />}
          </BonusOffer>
        )}
      </Legend>

      {/* ACCORDIONS */}
      <AccordionWrapper
        id="carrierPricingWrapper"
        allowMultipleExpanded
        allowZeroExpanded
      >
        
        {(offer.monthlyPriceReturn !== null
          && offer.carrierUpfrontPriceReturn !== null) && (
          <AccordionDualPricing uuid={uuidv4()}>
            <AccordionHeading>
              <AccordionButton>
                <Preview>
                  <PreviewWrapper>
                    
                    {/* RETURN PHONE */}
                    <Detail>
                      <DetailLabel>{labels['3bLjilvbvaT8j25BzHHJty']}</DetailLabel>
                      <PriceLarge className="monthlyPrice" data-testid="fomatted-monthly-price">
                        <IntlProvider locale={`${locale}-CA`}>
                          {locale === 'en' ? (
                            <FormattedNumber
                              value={offer.monthlyPriceReturn}
                                // eslint-disable-next-line react/style-prop-object
                              style="currency"
                              currency="CAD"
                              maximumFractionDigits={2}
                              minimumFractionDigits={2}
                            />
                          ) : (
                            <FormattedNumberParts
                              value={offer.monthlyPriceReturn}
                                // eslint-disable-next-line react/style-prop-object
                              style="currency"
                              currency="CAD"
                            >
                              {parts => (
                                <>
                                  {parts.map(part => part.value)}
                                </>
                              )}
                            </FormattedNumberParts>
                          )}
                        </IntlProvider>
                        <span className="small">{labels['5oSjrNbuCCZdOCFdkEYKhb']}</span>
                      </PriceLarge>
                    </Detail>

                    {/* UPFRONT COST */}
                    <Detail>
                      <DetailLabel>{labels['6KcxYb1EQRl64daz8jTntc']}</DetailLabel>
                      <Price className="carrierUpFrontPrice">
                        <IntlProvider locale={`${locale}-CA`}>
                          {locale === 'en' ? (
                            <FormattedNumber
                              value={offer.carrierUpfrontPriceReturn}
                                  // eslint-disable-next-line react/style-prop-object
                              style="currency"
                              currency="CAD"
                              maximumFractionDigits={2}
                              minimumFractionDigits={2}
                            />
                          ) : (
                            <FormattedNumberParts
                              value={offer.carrierUpfrontPriceReturn}
                                  // eslint-disable-next-line react/style-prop-object
                              style="currency"
                              currency="CAD"
                            >
                              {parts => (
                                <>
                                  {parts.map(part => part.value)}
                                </>
                              )}
                            </FormattedNumberParts>
                          )}
                        </IntlProvider>
                        {/* Add asterisks if needed for legal text at bottom of panel */}
                        {offer?.phoneCarrierTierReturn?.description?.raw?.includes('*')
                          ? '*'
                          : ''}
                      </Price>
                    </Detail>
                  </PreviewWrapper>
              
                  <ButtonGraphicWrapper>
                    <p>{labels['1jW81ehfwpmEcOxTmwxBBA']}</p>
                    {' '}
                    <ButtonGraphic className="arrowButton" />
                  </ButtonGraphicWrapper>
                </Preview>
              </AccordionButton>
            </AccordionHeading>

            {/* TIER DETAILS */}
            <ExpandedTierDetails>
              <Disclaimer>
                <RichText
                  document={offer?.phoneCarrierTierReturn?.description}
                  options={tierBodyOptions}
                />
                <RichText
                  document={offer?.phoneCarrierTierReturn?.legalDetails?.contentBody}
                  options={tierBodyOptions}
                />
              </Disclaimer>
            </ExpandedTierDetails>
          </AccordionDualPricing>
        )}

        <AccordionDualPricing uuid={uuidv4()}>
          <AccordionHeading>
            <AccordionButton>
              <Preview>
                <PreviewWrapper>

                  {/* KEEP PHONE */}
                  <Detail>
                    <DetailLabel>{labels['1LSwNEmjYAfJgn84UoZiUE']}</DetailLabel>
                    <PriceLarge className="monthlyPrice">
                      <IntlProvider locale={`${locale}-CA`}>
                        {locale === 'en' ? (
                          <FormattedNumber
                            value={offer.monthlyPrice}
                            // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currency="CAD"
                            maximumFractionDigits={2}
                            minimumFractionDigits={2}
                          />
                        ) : (
                          <FormattedNumberParts
                            value={offer.monthlyPrice}
                            // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currency="CAD"
                          >
                            {parts => (
                              <>
                                {parts.map(part => part.value)}
                              </>
                            )}
                          </FormattedNumberParts>
                        )}
                      </IntlProvider>
                      <span className="small">{labels['5oSjrNbuCCZdOCFdkEYKhb']}</span>
                    </PriceLarge>
                  </Detail>

                  {/* UPFRONT COST */}
                  <Detail>
                    <DetailLabel>{labels['6KcxYb1EQRl64daz8jTntc']}</DetailLabel>
                    <Price className="carrierUpFrontPrice">
                      <IntlProvider locale={`${locale}-CA`}>
                        {locale === 'en' ? (
                          <FormattedNumber
                            value={offer.carrierUpfrontPrice}
                                // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currency="CAD"
                            maximumFractionDigits={2}
                            minimumFractionDigits={2}
                          />
                        ) : (
                          <FormattedNumberParts
                            value={offer.carrierUpfrontPrice}
                                // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currency="CAD"
                          >
                            {parts => (
                              <>
                                {parts.map(part => part.value)}
                              </>
                            )}
                          </FormattedNumberParts>
                        )}
                      </IntlProvider>
                      {/* Add asterisks if needed for legal text at bottom of panel */}
                      {offer?.phoneCarrierTier?.description?.raw?.includes('*')
                        ? '*'
                        : ''}
                    </Price>
                  </Detail>
                </PreviewWrapper>
            
                <ButtonGraphicWrapper>
                  <p>{labels['1jW81ehfwpmEcOxTmwxBBA']}</p>
                  {' '}
                  <ButtonGraphic className="arrowButton" />
                </ButtonGraphicWrapper>
              </Preview>
            </AccordionButton>
          </AccordionHeading>

          {/* TIER DETAILS */}
          <ExpandedTierDetails>
            <Disclaimer>
              <RichText
                document={offer?.phoneCarrierTier?.description}
                options={tierBodyOptions}
              />
              <RichText
                document={offer?.phoneCarrierTier?.legalDetails?.contentBody}
                options={tierBodyOptions}
              />
            </Disclaimer>
          </ExpandedTierDetails>
        </AccordionDualPricing>
      </AccordionWrapper>
      <InventoryWrapper $renderInventory={renderInventory}>
        {renderInventory ? (
          <InventoryLogos
            activeStore={activeStore}
            inventoryLabels={inventoryLabels}
            apiError={apiError}
            inventoryStatus={inventoryStatus}
            selectedCarrier={selectedCarrier}
            offer={offer}
          />
        ) : (
          ''
        )}
      </InventoryWrapper>
    </CarrierOfferForPhone>
  );
};

export default CarrierPricingDetails;
